import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { chartColors, chartFonts, sharedCartesianGridProps, sharedAxisProps } from "./chartTheme";
import useReducedMotion from "./useReducedMotion";

// "2026-08-15" -> "Aug 15" — the full ISO date is too wide to tick densely
// without overlapping at 30/90-day ranges.
function formatTickDate(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

// Picks up to `maxTicks` dates, evenly spaced by index. Recharts'
// interval="preserveStartEnd" (the previous approach) picks intermediate
// ticks by greedily fitting as many as possible within minTickGap, then
// force-adds the true last data point regardless of whether it lands on
// that spacing — producing a visibly larger final gap whenever the data
// length doesn't divide evenly into the greedy pattern (confirmed live on
// both the 30d and 90d ranges). Sampling indices evenly up front guarantees
// the last tick is already exactly `data.length - 1` by construction, no
// forcing needed, so every gap — including the last — is the same size.
//
// The 7d range (7 points) surfaced a second, subtler issue with the same
// sampling approach: reducing 7 points to 6 ticks forces exactly one gap to
// be twice as wide as the rest — 7 doesn't divide evenly by 6 (or by any
// smaller tick count above 1), so no distribution of 6 ticks across 7
// points can avoid one uneven gap; it's a property of the numbers involved,
// not a smarter-rounding problem. Below the `maxTicks` count is the wrong
// question to ask at all — just show every date, which is exactly what the
// 7-day range should look like anyway.
function evenlySpacedTicks(data, maxTicks = 6) {
  if (data.length <= 7) return data.map((d) => d.date);
  const ticks = [];
  for (let i = 0; i < maxTicks; i++) {
    const idx = Math.round((i * (data.length - 1)) / (maxTicks - 1));
    ticks.push(data[idx].date);
  }
  return Array.from(new Set(ticks));
}

function DarkTooltip({ active, payload, label, series }) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bg-rf-bg-secondary border border-rf-border rounded-lg px-3 py-2.5 shadow-lg">
      <div className="text-[11px] text-rf-text-muted font-mono mb-1.5">{label}</div>
      <div className="flex flex-col gap-1">
        {series.map((s) => {
          const entry = payload.find((p) => p.dataKey === s.dataKey);
          const value = entry ? entry.value : null;
          return (
            <div key={s.dataKey} className="flex items-center gap-2 text-xs">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
              <span className="text-rf-text-secondary">{s.label}:</span>
              <span className="text-rf-text-primary font-mono font-medium">
                {value === null || value === undefined ? "—" : value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Reusable trend line chart, shared by both the PR volume/resolution chart
 * (two series) and the complexity chart (one series, fixed 0-10 domain).
 *
 * `series`: [{ dataKey, label, color, dashed?, connectNulls? }]
 */
function TrendLineChart({ data, series, yDomain, ariaLabel, height = 260 }) {
  const reducedMotion = useReducedMotion();

  return (
    <div>
      <div role="img" aria-label={ariaLabel} style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 28, left: -12, bottom: 0 }}>
            <CartesianGrid {...sharedCartesianGridProps} />
            <XAxis
              dataKey="date"
              {...sharedAxisProps}
              tickFormatter={formatTickDate}
              ticks={evenlySpacedTicks(data)}
            />
            <YAxis {...sharedAxisProps} domain={yDomain} allowDecimals={false} />
            <Tooltip
              content={<DarkTooltip series={series} />}
              cursor={{ stroke: chartColors.grid }}
            />
            {series.map((s) => (
              <Line
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                strokeDasharray={s.dashed ? "6 3" : undefined}
                dot={false}
                connectNulls={s.connectNulls !== false}
                isAnimationActive={!reducedMotion}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <details className="mt-2">
        <summary className="text-[11px] text-rf-text-muted cursor-pointer hover:text-rf-text-secondary">
          Show data table
        </summary>
        {/* Fixed (not max-) height so the area is the same size at every
            range — matches the 7d range's natural 7-row height. Without a
            fixed height, 7d (short) and 30d/90d (capped at the max) render
            at visibly different sizes. */}
        <div className="mt-2 h-[164px] overflow-y-auto overflow-x-auto">
          <table className="w-full text-xs" style={{ fontFamily: chartFonts.mono }}>
            <thead className="sticky top-0 bg-rf-bg-secondary">
              <tr className="text-rf-text-muted text-left">
                <th className="pr-4 py-1 font-normal">Date</th>
                {series.map((s) => (
                  <th key={s.dataKey} className="pr-4 py-1 font-normal">{s.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.date} className="text-rf-text-secondary">
                  <td className="pr-4 py-0.5">{row.date}</td>
                  {series.map((s) => (
                    <td key={s.dataKey} className="pr-4 py-0.5">
                      {row[s.dataKey] === null || row[s.dataKey] === undefined ? "—" : row[s.dataKey]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

export default TrendLineChart;
